/**
 * Clinical data modules: visits, notes, vitals, definitions, readings, labUpload.
 */
import { fetchApi, fetchFileApi, API_BASE_URL, generateRequestId, ApiError } from '../client';
import { secureStorage } from '../../secureStorage';
import { getFriendlyErrorMessage } from '../../errorMapping';
import type {
  Visit,
  ClinicalNote,
  ClinicalNoteCreateRequest,
  Vitals,
  CreateVitalsRequest,
  ReadingCategory,
  ReadingDefinition,
  VitalsExportRequest,
  CreateReadingRequest,
} from '../../../types';

export const visitsModule = {
  create: (data: Partial<Visit>) =>
    fetchApi<Visit>('/api/visits', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Visit>) =>
    fetchApi<Visit>(`/api/visits/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  getById: (id: string) =>
    fetchApi<Visit>(`/api/visits/${id}`),

  getWithVitals: (patientId: string) =>
    fetchApi<any>(`/api/visits/patient/${patientId}`),

  updateVitals: (
    visitId: string,
    vitalsData: {
      systolicBP?: number | null;
      diastolicBP?: number | null;
      pulse?: number | null;
      temperature?: number | null;
      spO2?: number | null;
      respiratoryRate?: number | null;
      weight?: number | null;
    },
    editReasonCategory?: string,
    editReasonDetail?: string,
    structuredDataJson?: string
  ) =>
    fetchApi<any>(`/api/visits/${visitId}/vitals`, {
      method: 'PATCH',
      body: JSON.stringify({ ...vitalsData, editReasonCategory, editReasonDetail, structuredDataJson }),
    }),

  getAuditTrail: (visitId: string) =>
    fetchApi<any>(`/api/visits/${visitId}/audit-trail`),

  getFieldHistory: (visitId: string, fieldName: string) =>
    fetchApi<any>(`/api/visits/${visitId}/field-history/${fieldName}`),
};

export const notesModule = {
  getByPatient: (patientId: string) =>
    fetchApi<ClinicalNote[]>(`/api/notes?patientId=${patientId}`),

  create: (data: ClinicalNoteCreateRequest) =>
    fetchApi<ClinicalNote>('/api/notes', { method: 'POST', body: JSON.stringify(data) }),
};

export const vitalsModule = {
  getByPatient: (patientId: string) =>
    fetchApi<Vitals[]>(`/api/vitals?patientId=${patientId}`),

  create: (data: CreateVitalsRequest) =>
    fetchApi<Vitals>('/api/vitals', { method: 'POST', body: JSON.stringify(data) }),
};

export const definitionsModule = {
  get: (category: ReadingCategory) =>
    fetchApi<ReadingDefinition[]>(`/api/definitions?category=${category}`),
};

export const readingsModule = {
  getVitals: (patientId: string) =>
    fetchApi<any[] & { data?: any[] }>(`/api/vitals?patientId=${patientId}`),

  getBloodLabs: (patientId: string) =>
    fetchApi<any[] & { data?: any[] }>(`/api/vitals?patientId=${patientId}&type=blood`),

  getUrineLabs: (patientId: string) =>
    fetchApi<any[] & { data?: any[] }>(`/api/vitals?patientId=${patientId}&type=urine`),

  createVitals: (data: CreateReadingRequest) =>
    fetchApi<any>('/api/readings/vitals', { method: 'POST', body: JSON.stringify(data) }),

  createBloodLabs: (data: CreateReadingRequest) =>
    fetchApi<any>('/api/readings/blood-labs', { method: 'POST', body: JSON.stringify(data) }),

  createUrineLabs: (data: CreateReadingRequest) =>
    fetchApi<any>('/api/readings/urine-labs', { method: 'POST', body: JSON.stringify(data) }),

  exportVitals: (data: VitalsExportRequest) =>
    fetchFileApi('/api/readings/vitals/export', data),
};

export const labUploadModule = {
  createSession: (patientId: string, consentGiven: boolean) =>
    fetchApi<{ sessionId: string; token: string; expiresAt: string }>(
      `/api/lab-upload/session?patientId=${patientId}&consentGiven=${consentGiven}`,
      { method: 'POST' }
    ),

  invalidateSession: (sessionId: string) =>
    fetchApi<void>(`/api/lab-upload/session/${sessionId}/invalidate`, { method: 'POST' }),

  finalizeSession: (sessionId: string) =>
    fetchApi<void>(`/api/lab-upload/session/${sessionId}/finalize`, { method: 'POST' }),

  getUploadUrl: (token: string) => `${API_BASE_URL}/api/lab-upload/upload/${token}`,

  getPreviewUrl: (filePath: string) =>
    fetchApi<{ url: string }>(
      `/api/lab-upload/preview?filePath=${encodeURIComponent(filePath)}`
    ),

  getPatientUploads: (patientId: string) =>
    fetchApi<any[]>(`/api/lab-upload/patient/${patientId}`),

  downloadFile: async (filePath: string) => {
    try {
      const token = secureStorage.getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(
        `${API_BASE_URL}/api/lab-upload/download?filePath=${encodeURIComponent(filePath)}`,
        { headers }
      );
      if (!response.ok) throw new Error(`Failed to download: ${response.statusText}`);
      return { data: await response.blob() };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Download failed' };
    }
  },

  directUpload: async (patientId: string, files: File[], consentGiven: boolean) => {
    const formData = new FormData();
    formData.append('patientId', patientId);
    formData.append('consentGiven', consentGiven.toString());
    files.forEach(file => formData.append('file', file));

    const token = secureStorage.getAuthToken();
    const headers: Record<string, string> = {
      'X-Request-ID': generateRequestId(),
      'X-Client-Version': '1.0.0',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(`${API_BASE_URL}/api/lab-upload/direct-upload`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new ApiError(err.message || 'Upload failed', response.status);
      }
      const data = await response.json();
      return { data: data.data };
    } catch (error) {
      if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
      return { error: 'Network error' };
    }
  },

  getExtractionSummary: (patientId: string) =>
    fetchApi<any>(`/api/lab-upload/patient/${patientId}/extraction-summary`),

  getExtractionHistory: (patientId: string, limit = 10) =>
    fetchApi<any[]>(
      `/api/lab-upload/patient/${patientId}/extraction-history?limit=${limit}`
    ),

  getTrendData: (patientId: string, metric: string, months = 6) =>
    fetchApi<any>(
      `/api/lab-upload/patient/${patientId}/trend-data?metric=${metric}&months=${months}`
    ),

  verifyExtraction: (docId: string, confirmed: boolean, notes = '') =>
    fetchApi<void>(`/api/lab-upload/${docId}/verify-extraction`, {
      method: 'POST',
      body: { confirmed, notes } as any,
    }),

  updateExtraction: (
    docId: string,
    extractedVitals: any,
    extractedLabs: any,
    extractedMedications: any
  ) =>
    fetchApi<any>(`/api/lab-upload/${docId}/extraction`, {
      method: 'PUT',
      body: { extractedVitals, extractedLabs, extractedMedications } as any,
    }),

  getClinicalSummary: (patientId: string) =>
    fetchApi<any>(`/api/lab-upload/patient/${patientId}/clinical-summary`),

  analyzeDocument: (docId: string) =>
    fetchApi<any>(`/api/lab-upload/${docId}/analyze`, { method: 'POST' }),

  generateSummary: (docId: string, patientId: string) =>
    fetchApi<any>(
      `/api/lab-upload/${docId}/generate-summary?patientId=${patientId}`,
      { method: 'POST' }
    ),
};
